import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { ScrollText, X } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Checkbox } from '../../submission/components/ui/checkbox'

interface TermsDisclaimerDialogProps {
  open: boolean
  onAccept: () => void
  onClose?: () => void
}

export default function TermsDisclaimerDialog({
  open,
  onAccept,
  onClose,
}: TermsDisclaimerDialogProps) {
  const [acknowledgements, setAcknowledgements] = useState({
    termsOfUse: false,
    termsOfService: false,
    privacyPolicy: false,
  })

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose?.()
      }
    },
    [onClose],
  )

  const setAcknowledgement = (
    key: keyof typeof acknowledgements,
    value: boolean,
  ) => {
    setAcknowledgements((prev) => ({ ...prev, [key]: value }))
  }

  const allChecked =
    acknowledgements.termsOfUse &&
    acknowledgements.termsOfService &&
    acknowledgements.privacyPolicy

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[600px] max-h-[92vh] overflow-hidden sm:rounded-3xl"
      >
        <DialogClose
          className="absolute right-4 top-4 rounded-full p-2 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </DialogClose>

        <DialogHeader>
          <DialogTitle>Curator Portal Terms of Use</DialogTitle>
        </DialogHeader>

        <div className="max-h-[75vh] overflow-y-auto pr-4">
          <div className="flex flex-col items-center justify-center py-8 mb-6 animate-reveal-from-bottom pt-14">
            <div className="flex items-center justify-center mb-4">
              <ScrollText className="h-12 w-12 text-brand-primary" />
            </div>
            <h3 className="font-semibold text-neutral-900 mb-2 text-2xl">
              Curator Portal Terms of Use
            </h3>
            <p className="text-center text-sm text-neutral-700 max-w-md">
              Please review and agree the terms of use to continue with the
              curator onboarding process.
            </p>
          </div>

          <div className="space-y-4 text-sm text-neutral-700 px-4">
            <p>
              <strong>Effective Date: 9 December 2025</strong>
            </p>
            <p>
              These Curator Terms of Use (these “<u>Terms</u>”) govern your
              access to and use of the CURATED curator portal and platform
              (together, the “<u>Platform</u>”), as well as all related
              services, features, and functionalities made available by the
              CURATED (collectively, the “<u>Services</u>”). For purposes of
              these Terms, “CURATED” (“<u>CURATED</u>”, “<u>we</u>”, “<u>us</u>
              ”, or “<u>our</u>”) refers to <strong>Gahinga Inc.</strong>, a
              Company incorporated under the laws of Panama and its affiliates,
              subsidiaries, and service providers acting on its behalf. These
              Terms are supplementary to, and incorporate by reference, the
              CURATED Terms of Service (the “<u>General Terms</u>”). In the
              event of any conflict between these Terms and the Platform Terms,
              the Platform Terms shall prevail unless expressly stated
              otherwise. By registering as a curator, accessing or using the
              Platform or Services, or submitting any deal, project, or sale for
              review, approval, or listing, you (the “<u>Curator,</u>” or “
              <u>you</u>”) expressly acknowledge and agree to be legally bound
              by these Terms, the General Terms, any applicable compliance
              policies, guidelines and agreements.
            </p>
            <p>
              <strong>1. Access and Eligibility</strong>
            </p>
            <p>
              1.1 Eligibility. Access to the Curator Portal is strictly limited
              to individuals or entities (“<u>Curators</u>”) that have been
              expressly approved in writing by the CURATED following the
              successful completion of all onboarding procedures, including but
              not limited to Know-Your-Customer (“KYC”), Know-Your-Business
              (“KYB”), anti-money-laundering and counter-terrorist-financing
              (“AML/CTF”) checks, sanctions screenings, and any other compliance
              verifications or due diligence measures that the CURATED may
              determine necessary in its sole and absolute discretion. The
              Curator acknowledges that approval is conditional, may be revoked
              at any time, and does not confer any continuing or vested right of
              access.
            </p>
            <p>
              1.2 Ongoing Obligations. The Curator shall promptly (and in any
              event within five (5) business days) notify the CURATED in writing
              of any change that may render any information, documentation, or
              declaration previously provided during onboarding inaccurate,
              incomplete, or misleading. This includes, without limitation, any
              change in the Curator’s legal name, ownership, control, beneficial
              ownership, management, registered address, jurisdiction of
              incorporation or residence, regulatory status, or any event that
              could reasonably affect the Curator’s eligibility, reputation, or
              compliance standing.
            </p>
            <p>
              1.3 Discretionary Access. Access to and participation in the
              Curator Portal are privileges granted at the sole discretion of
              the CURATED. The CURATED may, at any time and without prior notice
              or liability, review, approve, reject, suspend, restrict, revoke,
              or remove any Curator, Sale, or aspect of the Curator Portal,
              temporarily or permanently, for any reason or no reason, including
              actual or suspected non-compliance with these Terms, the Platform
              Terms, applicable law, or any regulatory directive. The Curator
              expressly waives any claim arising from or relating to any such
              action or decision of the CURATED.
            </p>
            <p>
              <strong>2. Use of the Curator Portal</strong>
            </p>
            <p>
              2.1 Purpose. The Curator Portal enables approved Curators to
              originate, submit, administer, and monitor token sales or
              analogous fundraising activities (each, a “<u>Sale</u>”) conducted
              by or on behalf of an independent project, protocol, foundation,
              or business entity (each, a “<u>Project</u>”). Each Sale shall be
              deemed to be offered, issued, and conducted solely by the relevant
              Project, and not by the CURATED. The Curator’s role is limited to
              facilitating the submission, coordination, or hosting of such Sale
              through the Curator Portal. The CURATED is an issuer, underwriter,
              broker, arranger, or promoter of any Sale.
            </p>
            <p>
              2.2 Project Authorization, Approvals, and Jurisdictional
              Restrictions
            </p>
            <p>
              2.2.1 No Sale may be proposed, submitted, or hosted through the
              Curator Portal unless it has been duly authorized in writing by
              the relevant Project and satisfies all applicable legal,
              technical, and compliance prerequisites as determined by the
              CURATED in its sole discretion.
            </p>
            <p>
              2.2.2 As a condition precedent to the listing, hosting, or public
              announcement of any Sale, the Curator shall obtain from the
              Project and provide to the CURATED satisfactory evidence of the
              Project’s authorization of the Sale, including without limitation;
              (i) written approval of the Sale and the Curator’s engagement;
              (ii) a legal opinion from qualified external counsel confirming
              the legality and regulatory characterization of the Sale and any
              associated digital assets; (iii) a list of all restricted or
              excluded jurisdictions by the Project, and (iv) any other
              supporting documents, licenses, or approvals reasonably required
              by the CURATED to verify compliance with applicable laws,
              including without limitation, securities, virtual asset, AML/CTF,
              and financial-promotion laws.
            </p>
            <p>
              2.2.3 The Curator shall ensure that all marketing, promotional,
              and communications materials (collectively, the “
              <u>Marketing Materials</u>”) relating to any Sale or Project are
              accurate, not misleading, and fully compliant with all applicable
              laws, regulations, and guidelines. Prior to any publication,
              distribution, or public release, all Marketing Materials shall be
              reviewed and approved in writing by the relevant Project and,
              where applicable, by any required third parties, advisors, or
              legal counsel. The Curator shall not disseminate or authorize the
              dissemination of any Marketing Materials without such prior
              written approval.
            </p>
            <p>
              2.2.4 The Curator represents and warrants that the Project has
              granted all necessary rights, licenses, and intellectual property
              permissions, including rights to use its name, trademarks, logos,
              branding, and proprietary materials, in connection with the Sale,
              and that such use does not infringe or violate the rights of any
              third party.
            </p>
            <p>
              2.2.5 The CURATED reserves the right to request evidence of
              compliance with this Section at any time, to require updates or
              clarifications to the Project’s restricted jurisdiction list, and
              to suspend, withhold, or remove any Sale pending satisfactory
              verification.
            </p>
            <p>
              <strong>3. Curator Responsibilities</strong>
            </p>
            <p>
              The Curator shall ensure that all information, documents, and
              materials relating to any Sale are true, complete, accurate, and
              not misleading in any respect. The Curator bears sole
              responsibility for ensuring that each Sale, and all related
              activities, comply with all applicable laws, regulations, and
              regulatory guidance, including those governing securities, virtual
              assets, AML/CTF, sanctions, consumer protection, advertising, and
              financial promotion. Upon discovery or notice of any actual or
              suspected non-compliance, misrepresentation, or deficiency, the
              Curator shall immediately suspend or withdraw the affected Sale,
              take all necessary corrective action, and fully cooperate with the
              CURATED in any related inquiry, investigation, or remediation.
            </p>
            <p>
              <strong>4. Brand License and Partnership Communications</strong>
            </p>
            <p>
              4.1. Grant of License. The Curator hereby grants CURATED a
              non-exclusive, worldwide, royalty-free, fully paid-up,
              transferable and sublicensable license to use, reproduce, display,
              distribute, publish, and otherwise make available the Curator’s
              name, trademarks, service marks, logos, brand identifiers, and any
              related brand assets (collectively, the “<u>Curator Branding</u>”)
              for the purposes of: (i) identifying the Curator as a participant,
              collaborator, or partner on the Platform; (ii) displaying the
              Curator’s profile, campaigns, deals, or related activities on the
              Platform; (iii) marketing, promoting, or publicizing any
              Curator-led sale, campaign, or activity on or in connection with
              the Platform; and (iv) creating, publishing, and distributing
              partnership announcements, promotional materials, and listings
              across the CURATED’s websites, social media channels, and
              marketing or communication platforms.
            </p>
            <p>
              4.2 Scope of Use. The license granted herein includes the right
              for CURATED to: (i) modify, adapt, or otherwise make derivative
              uses of the Curator Branding solely to the extent necessary for
              formatting, sizing, technical integration, or placement within the
              Platform or related materials, provided that such modifications do
              not materially alter, distort, mislead, or disparage the Curator
              Branding; (ii) use the Curator Branding in textual, visual,
              audiovisual, digital, or printed promotional, informational, or
              marketing materials in connection with the Platform; and (iii)
              sublicense the rights granted under this Section to the CURATED’s
              Affiliates, technical integration partners, marketing partners,
              and third-party service providers solely for purposes of
              performing services on behalf of, or for the benefit of, the
              CURATED in connection with the Platform, provided that CURATED
              shall give the Curator prior notice in writing of any such
              sublicensing and shall ensure that all such parties use the
              Curator Branding in a manner consistent with these Terms; and (iv)
              make factual and non-misleading public communications relating to
              the Curator’s participation in, partnership with, or activities on
              or in connection with the Platform.
            </p>
            <p>
              4.3 No Endorsements. Nothing in these Terms, nor any use of the
              Curator Branding by CURATED, shall be construed as an endorsement,
              guarantee, sponsorship, or certification by the Curator of CURATED
              or its products or services. The Curator shall not make, and shall
              ensure that its Affiliates and representatives do not make, any
              statement or implication that CURATED endorses, guarantees,
              sponsors, or certifies the Curator or any of its products or
              services.
            </p>
            <p>
              4.4 Revocation of License. The Curator may revoke the license
              granted under this Section upon written notice to CURATED;
              provided that: (i) CURATED shall have a reasonable wind-down
              period of fourteen (14) days from receipt of such notice to
              remove, replace, or update existing materials containing the
              Curator Branding; and (ii) CURATED may retain and continue to
              display, in perpetuity, any historical content, including without
              limitation, announcements, publications, social media posts,
              campaign pages, marketing collateral, or archived Platform content
              created prior to the effective date of revocation, without
              obligation to modify or remove such materials.
            </p>
            <p>
              4.5 Representations and Warranties. The Curator represents and
              warrants that: (i) it has full right, power, and authority to
              grant the license and rights set forth in this Section; (ii) the
              Curator Branding do not infringe, misappropriate, or violate any
              intellectual property or other rights of any third party; and
              (iii) CURATED’s exercise of the licensed rights in accordance with
              these Terms will not breach any contractual obligations binding on
              the Curator or violate any applicable laws.
            </p>
            <p>
              <strong>4A. CURATED’s Intellectual Property</strong>
            </p>
            <p>
              Unless expressly agreed in writing by the CURATED, nothing in
              these Terms shall be construed as granting the Curator any
              license, right, or permission to use the CURATED’s name,
              trademarks, logos, domain names, proprietary materials, or any
              other intellectual property, whether for promotional purposes or
              otherwise.
            </p>
            <p>
              <strong>5. Prohibited Conduct</strong>
            </p>
            <p>
              The Curator shall not submit false, inaccurate, or misleading
              information; circumvent or attempt to circumvent any KYC, KYB, or
              compliance requirement; host or facilitate Sales involving
              sanctioned persons or jurisdictions; misrepresent its relationship
              with the CURATED or any Project; misuse, disrupt, or interfere
              with the Platform’s systems or technical infrastructure; or
              provide, or purport to provide, any investment, legal, financial,
              or similar advice in connection with any Sale.
            </p>
            <p>
              <strong>6. CURATED Role and Disclaimers</strong>
            </p>
            <p>
              The CURATED operates solely as a technology and infrastructure
              service provider and does not issue, broker, underwrite, arrange,
              or provide advice or recommendations in relation to any Sale. The
              listing, hosting, or availability of any Sale on the Platform
              shall not be construed as an endorsement, recommendation,
              guarantee, or approval by the CURATED, which owes no fiduciary,
              advisory, or similar duties to any Curator, Project, or
              participant. The Curator shall not state or imply that the CURATED
              has verified, approved, or guaranteed any Sale and shall indemnify
              and hold harmless the CURATED from and against any loss, claim, or
              liability arising out of or related to any such misrepresentation.
            </p>
            <p>
              <strong>7. Breach and Indemnification</strong>
            </p>
            <p>
              Without prejudice to any other rights or remedies, if the Curator
              breaches, in whole or in part, any provision of these Terms, the
              CURATED may take such action as it deems appropriate, including
              suspending or terminating the Curator’s access, these Terms, or
              any related agreement, and/or pursuing legal action. The Curator
              shall fully indemnify, defend, and hold harmless the CURATED and
              its affiliates, shareholders, directors, officers, employees, and
              agents from and against any and all claims, demands, liabilities,
              damages, losses, costs, and expenses (including reasonable legal
              fees) arising out of or in connection with (i) any breach of these
              Terms by the Curator, (ii) any violation by the Curator of
              applicable law or third-party rights, or (iii) the Curator’s use
              or misuse of the Platform or Services.
            </p>
            <p>
              <strong>8. Waiver of Conflict</strong>
            </p>
            <p>
              The Curator acknowledges that the CURATED and its affiliates may
              act in multiple capacities, including CURATED and its affiliates
              may act in multiple capacities, including as curators, advisers,
              investors, or counterparties in other transactions, and may
              therefore have interests that differ from or conflict with those
              of the Curator. The Curator irrevocably waives any claim,
              objection, or right to challenge any such actual or potential
              conflict, subject to the CURATED’s compliance with any mandatory
              disclosure obligations under applicable law.
            </p>
            <p>
              <strong>9. Miscellaneous</strong>
            </p>
            <p>
              All matters relating to governing law, dispute resolution,
              amendments, notices, survival, and any other general provisions
              not expressly addressed in these Terms shall be determined in
              accordance with the corresponding provisions set out in the
              General Terms.
            </p>
          </div>

          <div className="px-4 pt-6 pb-2 space-y-3 text-sm text-neutral-800">
            <label className="flex items-start gap-3">
              <Checkbox
                id="curator-terms"
                checked={acknowledgements.termsOfUse}
                onCheckedChange={(checked) =>
                  setAcknowledgement('termsOfUse', Boolean(checked))
                }
                className="mt-1"
              />
              <span>
                I confirm that I have read and fully understood the Curator
                Portal Terms of Use and agree to be bound by them.
              </span>
            </label>

            <label className="flex items-start gap-3">
              <Checkbox
                id="tos"
                checked={acknowledgements.termsOfService}
                onCheckedChange={(checked) =>
                  setAcknowledgement('termsOfService', Boolean(checked))
                }
                className="mt-1"
              />
              <span>
                I confirm that I have read and fully understood the{' '}
                <a
                  href="https://curated.impossible.finance/terms"
                  className="text-brand-primary underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Service
                </a>{' '}
                and agree to be bound by them.
              </span>
            </label>

            <label className="flex items-start gap-3">
              <Checkbox
                id="privacy-policy"
                checked={acknowledgements.privacyPolicy}
                onCheckedChange={(checked) =>
                  setAcknowledgement('privacyPolicy', Boolean(checked))
                }
                className="mt-1"
              />
              <span>
                I confirm that I have read and fully understood the{' '}
                <a
                  href="https://curated.impossible.finance/privacy-policy"
                  className="text-brand-primary underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>{' '}
                and agree to be bound by them.
              </span>
            </label>
          </div>

          <div className="flex justify-center pt-6 pb-4">
            <Button
              onClick={onAccept}
              disabled={!allChecked}
              className="bg-brand-primary hover:bg-brand-primary/90 text-white w-[320px]"
            >
              Continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
